import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldAlert, Fingerprint, Lock, ScanFace, KeyRound, Crown } from "lucide-react";
import { toast } from "sonner";

const HERO_BG =
    "https://static.prod-images.emergentagent.com/jobs/8041819d-7954-4bde-9fe6-e6847159f659/images/b346a804f019c4ba1d2f53ce4b43f3e0f1dbade357bb64ed15ab2e60c7f675ec.png";

export default function IdentityAnchor() {
    const nav = useNavigate();
    const [step, setStep] = useState(1);
    const [callsign, setCallsign] = useState("");
    const [pin, setPin] = useState("");
    const [otp, setOtp] = useState("");
    const [loading, setLoading] = useState(false);

    const proceedToOTP = (e) => {
        e.preventDefault();
        if (!callsign || pin.length < 4) {
            toast.error("Callsign and 4+ digit PIN required");
            return;
        }
        setLoading(true);
        setTimeout(() => {
            setLoading(false);
            setStep(2);
            toast("OTP dispatched to your anchor device", { description: "Demo code: enter any 6 digits" });
        }, 500);
    };

    const verify = (e) => {
        e.preventDefault();
        if (otp.length < 6) {
            toast.error("Enter the 6-digit anchor code");
            return;
        }
        setLoading(true);
        localStorage.setItem("eigoo_anchor", "verified");
        localStorage.setItem("eigoo_callsign", callsign || "OPERATOR");
        setTimeout(() => {
            setLoading(false);
            toast.success("Identity anchored. Welcome, Operator.");
            nav("/");
        }, 400);
    };

    return (
        <div className="min-h-screen flex items-stretch relative overflow-hidden grid-bg" data-testid="anchor-page">
            <div className="hidden lg:flex flex-1 flex-col justify-between relative p-12">
                <div className="absolute inset-0 opacity-30">
                    <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-br from-black via-black/60 to-transparent" />

                <div className="relative z-10">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 border-2 border-gold flex items-center justify-center pulse-gold">
                            <Crown className="w-6 h-6 text-gold" />
                        </div>
                        <div>
                            <div className="heading-display text-xl">EIGOO</div>
                            <div className="label-tag">Digital Marketing</div>
                        </div>
                    </div>
                </div>

                <div className="relative z-10 max-w-xl">
                    <div className="label-tag mb-4 text-gold">/// SOVEREIGN HULL — SUPERIOR BRAND FLAGSHIP</div>
                    <h1 className="heading-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">
                        Industrial brand <span className="text-gold">command</span>.<br />
                        Lineage-grade <span className="text-gold">automation</span>.
                    </h1>
                    <p className="text-[#aaa] text-base leading-relaxed mb-8">
                        Flagship commanded by <span className="text-gold font-bold">Admiral Akanksha</span>, with
                        Heir Apparent <span className="text-gold font-bold">Ashairah</span> securing the lineage vault.
                        Unified telemetry across every campaign vessel — paired with the 60/30/10 sovereign revenue engine.
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                        <Stat label="Channels" value="12+" />
                        <Stat label="Hot Leads/wk" value="320" />
                        <Stat label="Agent Share" value="60%" />
                    </div>
                </div>

                <div className="relative z-10 label-tag text-[#666]">
                    MARKETING.EIGOOINC.NET // CLEARANCE OMEGA-GOLD
                </div>
            </div>

            <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-[#070707] border-l border-[#332A0C] relative">
                <div className="w-full max-w-md hud-panel p-8 fade-up" data-testid="anchor-card">
                    <div className="hud-corner-tl" /><div className="hud-corner-tr" />
                    <div className="hud-corner-bl" /><div className="hud-corner-br" />
                    <div className="scan-line" />
                    <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-2">
                            <ShieldAlert className="w-4 h-4 text-gold" />
                            <div className="label-tag">Identity Anchor // MFA</div>
                        </div>
                        <h2 className="heading-display text-3xl mb-1">
                            {step === 1 ? "Authenticate" : "Verify Anchor"}
                        </h2>
                        <p className="text-[#888] text-sm mb-6">
                            {step === 1
                                ? "Submit callsign and operator PIN to engage the marketing hull."
                                : "Enter the 6-digit code dispatched to your anchor device."}
                        </p>

                        {step === 1 ? (
                            <form onSubmit={proceedToOTP} className="space-y-4" data-testid="anchor-form-step1">
                                <div>
                                    <label className="label-tag block mb-2">Operator Callsign</label>
                                    <div className="flex items-center gap-2 bg-[#050505] border border-[#332A0C] focus-within:border-gold transition-colors px-3">
                                        <Fingerprint className="w-4 h-4 text-gold" />
                                        <input
                                            type="text" value={callsign}
                                            onChange={(e) => setCallsign(e.target.value)}
                                            placeholder="e.g. AKANKSHA-01"
                                            className="w-full bg-transparent py-3 outline-none text-sm tracking-wider uppercase border-0"
                                            style={{ border: 'none', boxShadow: 'none' }}
                                            data-testid="anchor-callsign"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="label-tag block mb-2">PIN</label>
                                    <div className="flex items-center gap-2 bg-[#050505] border border-[#332A0C] focus-within:border-gold transition-colors px-3">
                                        <KeyRound className="w-4 h-4 text-gold" />
                                        <input
                                            type="password" value={pin}
                                            onChange={(e) => setPin(e.target.value)}
                                            placeholder="••••••"
                                            className="w-full bg-transparent py-3 outline-none text-sm tracking-widest border-0"
                                            style={{ border: 'none', boxShadow: 'none' }}
                                            data-testid="anchor-pin"
                                        />
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    className="btn-gold w-full mt-2 disabled:opacity-50"
                                    disabled={loading}
                                    data-testid="anchor-submit"
                                >
                                    {loading ? "Dispatching..." : "Engage Anchor"}
                                </button>
                                <div className="text-[#666] text-xs text-center pt-2">
                                    Demo PIN: any 4+ characters
                                </div>
                            </form>
                        ) : (
                            <form onSubmit={verify} className="space-y-4" data-testid="anchor-form-step2">
                                <div>
                                    <label className="label-tag block mb-2">Anchor Code</label>
                                    <div className="flex items-center gap-2 bg-[#050505] border border-[#332A0C] focus-within:border-gold transition-colors px-3">
                                        <ScanFace className="w-4 h-4 text-gold" />
                                        <input
                                            type="text" inputMode="numeric" maxLength={6}
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                            placeholder="6-digit code"
                                            className="w-full bg-transparent py-3 outline-none text-2xl tracking-[0.5em] mono text-center text-gold border-0"
                                            style={{ border: 'none', boxShadow: 'none' }}
                                            data-testid="anchor-otp"
                                        />
                                    </div>
                                </div>
                                <button type="submit" className="btn-gold w-full" disabled={loading} data-testid="anchor-verify">
                                    {loading ? "Verifying..." : "Anchor Identity"}
                                </button>
                                <button type="button" className="btn-ghost-gold w-full" onClick={() => setStep(1)} data-testid="anchor-back">
                                    ← Back
                                </button>
                                <div className="text-[#666] text-xs text-center pt-2">
                                    Demo code: enter any 6 digits
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border-l border-gold pl-3">
            <div className="label-tag text-[#888]">{label}</div>
            <div className="heading-display text-2xl text-gold">{value}</div>
        </div>
    );
}
